import { useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AppSetting } from "@/types/settings";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";

interface EmailTemplateAccordionProps {
  settings: AppSetting[];
  onSaveSetting: (setting: AppSetting) => Promise<void>;
}

interface TemplateGroup {
  name: string;
  displayName: string;
  description: string;
  subject: AppSetting | null;
  body: AppSetting | null;
  placeholders: string[];
}

const EmailTemplateAccordion = ({ settings, onSaveSetting }: EmailTemplateAccordionProps) => {
  const { toast } = useToast();
  const [editingTemplates, setEditingTemplates] = useState<Record<string, boolean>>({});
  const [localSettings, setLocalSettings] = useState<Record<string, string>>({});
  const [savingTemplates, setSavingTemplates] = useState<Record<string, boolean>>({});
  const [savedTemplates, setSavedTemplates] = useState<Record<string, boolean>>({});

  // Group email template settings by template type
  const getTemplateGroups = (): TemplateGroup[] => {
    const templateTypes = [
      {
        name: 'WELCOME',
        displayName: 'Welcome Email',
        description: 'Sent when a new user registers',
        placeholders: ['{name}', '{app_url}']
      },
      {
        name: 'LISTING_APPROVED',
        displayName: 'Listing Approved',
        description: 'Sent when a service listing is approved',
        placeholders: ['{name}', '{listing_name}', '{app_url}']
      },
      {
        name: 'LISTING_REJECTED',
        displayName: 'Listing Rejected',
        description: 'Sent when a service listing is rejected',
        placeholders: ['{name}', '{listing_name}', '{rejection_reason}', '{app_url}']
      },
      {
        name: 'ACCOUNT_INACTIVE',
        displayName: 'Account Inactive',
        description: 'Sent when an account is deactivated',
        placeholders: ['{name}', '{app_url}']
      },
      {
        name: 'AUTH_CONFIRMATION',
        displayName: 'Email Confirmation',
        description: 'Sent for email address confirmation',
        placeholders: ['{name}', '{confirm_url}', '{app_url}']
      },
      {
        name: 'AUTH_MAGIC_LINK',
        displayName: 'Magic Link',
        description: 'Sent for passwordless sign-in',
        placeholders: ['{name}', '{sign_in_url}', '{app_url}']
      },
      {
        name: 'RESET_PASSWORD',
        displayName: 'Password Reset',
        description: 'Sent for password reset requests',
        placeholders: ['{name}', '{reset_link}', '{app_url}']
      },
      {
        name: 'CONTACT_FORM',
        displayName: 'Contact Form',
        description: 'Sent when someone submits a contact form',
        placeholders: ['{name}', '{email}', '{phone_number}', '{category}', '{message}', '{provider_name}', '{provider_id}', '{page_url}', '{app_url}']
      }
    ];

    return templateTypes.map(type => {
      const subjectKey = `EMAIL_TEMPLATE_${type.name}_SUBJECT`;
      const bodyKey = `EMAIL_TEMPLATE_${type.name}_BODY`;
      
      return {
        name: type.name,
        displayName: type.displayName,
        description: type.description,
        subject: settings.find(s => s.key === subjectKey) || null,
        body: settings.find(s => s.key === bodyKey) || null,
        placeholders: type.placeholders
      };
    });
  };

  const handleValueChange = (settingId: string, value: string) => {
    setLocalSettings(prev => ({ ...prev, [settingId]: value }));
    setEditingTemplates(prev => ({ ...prev, [settingId]: true }));
    // Clear saved state when editing
    setSavedTemplates(prev => ({ ...prev, [settingId]: false }));
  };

  const getDisplayValue = (setting: AppSetting) => {
    return localSettings[setting.id] !== undefined ? localSettings[setting.id] : setting.value;
  };

  const hasChanges = (templateName: string) => {
    const group = getTemplateGroups().find(g => g.name === templateName);
    if (!group) return false;
    
    return (group.subject && editingTemplates[group.subject.id]) || 
           (group.body && editingTemplates[group.body.id]);
  };

  const isSaved = (templateName: string) => {
    const group = getTemplateGroups().find(g => g.name === templateName);
    if (!group) return false;
    
    return (group.subject && savedTemplates[group.subject.id]) || 
           (group.body && savedTemplates[group.body.id]);
  };

  const saveTemplate = async (templateName: string) => {
    const group = getTemplateGroups().find(g => g.name === templateName);
    if (!group) return;

    setSavingTemplates(prev => ({ ...prev, [templateName]: true }));

    try {
      const promises = [];
      
      if (group.subject && editingTemplates[group.subject.id]) {
        const updatedSubject = {
          ...group.subject,
          value: localSettings[group.subject.id]
        };
        promises.push(onSaveSetting(updatedSubject));
      }
      
      if (group.body && editingTemplates[group.body.id]) {
        const updatedBody = {
          ...group.body,
          value: localSettings[group.body.id]
        };
        promises.push(onSaveSetting(updatedBody));
      }

      await Promise.all(promises);
      
      // Clear editing state and mark as saved
      if (group.subject) {
        setEditingTemplates(prev => ({ ...prev, [group.subject!.id]: false }));
        setSavedTemplates(prev => ({ ...prev, [group.subject!.id]: true }));
      }
      if (group.body) {
        setEditingTemplates(prev => ({ ...prev, [group.body!.id]: false }));
        setSavedTemplates(prev => ({ ...prev, [group.body!.id]: true }));
      }
      
      toast({
        title: "Template saved",
        description: `${group.displayName} template has been updated successfully.`,
      });

      // Clear saved state after 3 seconds to reset the button
      setTimeout(() => {
        if (group.subject) setSavedTemplates(prev => ({ ...prev, [group.subject!.id]: false }));
        if (group.body) setSavedTemplates(prev => ({ ...prev, [group.body!.id]: false }));
      }, 3000);
    } catch (error) {
      console.error("Error saving template:", error);
      toast({
        title: "Error",
        description: "Failed to save template. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSavingTemplates(prev => ({ ...prev, [templateName]: false }));
    }
  };

  const getButtonText = (templateName: string) => {
    if (savingTemplates[templateName]) return "Saving...";
    if (isSaved(templateName)) return "Saved ✓";
    if (hasChanges(templateName)) return "Save Template";
    return "No Changes";
  };

  const getButtonVariant = (templateName: string) => {
    if (isSaved(templateName)) return "default";
    if (hasChanges(templateName)) return "default";
    return "outline";
  };

  const templateGroups = getTemplateGroups();

  return (
    <div className="space-y-4">
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-2">Email Templates</h3>
        <p className="text-sm text-muted-foreground">
          Manage email templates that are sent to users. Each template supports placeholders for dynamic content.
        </p>
      </div>

      <Accordion type="single" collapsible className="space-y-4">
        {templateGroups.map((group) => (
          <AccordionItem key={group.name} value={group.name} className="border rounded-lg">
            <AccordionTrigger className="px-4 py-3 hover:no-underline">
              <div className="flex items-center justify-between w-full mr-4">
                <div className="text-left">
                  <div className="font-medium">{group.displayName}</div>
                  <div className="text-sm text-muted-foreground">{group.description}</div>
                </div>
                {hasChanges(group.name) && !isSaved(group.name) && (
                  <Badge variant="secondary" className="ml-2">
                    Unsaved changes
                  </Badge>
                )}
                {isSaved(group.name) && (
                  <Badge variant="default" className="ml-2 bg-green-500">
                    Recently saved
                  </Badge>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle className="text-base">Template Configuration</CardTitle>
                  <CardDescription>
                    Available placeholders: {group.placeholders.join(', ')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {group.subject && (
                    <div className="space-y-2">
                      <Label htmlFor={`subject-${group.name}`}>Subject Line</Label>
                      <Input
                        id={`subject-${group.name}`}
                        value={getDisplayValue(group.subject)}
                        onChange={(e) => handleValueChange(group.subject!.id, e.target.value)}
                        placeholder="Enter email subject..."
                        className={editingTemplates[group.subject.id] ? "border-whatsapp/40 bg-whatsapp-light/30" : ""}
                      />
                    </div>
                  )}
                  
                  {group.body && (
                    <div className="space-y-2">
                      <Label htmlFor={`body-${group.name}`}>Email Body</Label>
                      <Textarea
                        id={`body-${group.name}`}
                        value={getDisplayValue(group.body)}
                        onChange={(e) => handleValueChange(group.body!.id, e.target.value)}
                        className={`min-h-[200px] resize-y ${editingTemplates[group.body.id] ? "border-whatsapp/40 bg-whatsapp-light/30" : ""}`}
                        placeholder="Enter email body content..."
                      />
                    </div>
                  )}
                  
                  <div className="flex justify-end pt-4">
                    <Button
                      onClick={() => saveTemplate(group.name)}
                      disabled={!hasChanges(group.name) || savingTemplates[group.name]}
                      variant={getButtonVariant(group.name)}
                      className={isSaved(group.name) ? "bg-green-500 hover:bg-green-600" : ""}
                    >
                      {getButtonText(group.name)}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};

export default EmailTemplateAccordion;
